import {useDataQuery} from '@tnc/shared';
import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {distributorInsertT, distributorT} from '@/lib/types/distributor.types';
import {FormFieldT} from '@/lib/types/form.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderDistributorTableStats} from '@/lib/config/distributorTableConfig';
import {US_STATES_DATA} from '@/lib/jsonData/usStatesData';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useDistributors} from '@/providers/components/distributorsProvider';

import {
  ConfirmDeleteModalComponent,
  ModalFormComponent,
  SearchAndSelectComponent,
  StatsRowComponent,
} from '@/components';
import Page from '@/components/Page';
import {DataTableRpc} from '@/components/dataTableRpc';

const initialFormData: distributorInsertT = {
  created_by: '',
  date_created: '',
  date_modified: '',
  distributor_address: '',
  distributor_city: '',
  distributor_email: '',
  distributor_geo_points: null,
  distributor_license_number: '',
  distributor_metrc_api_key: '',
  distributor_name: '',
  distributor_phone: '',
  distributor_postal_code: '',
  distributor_state: '',
};

const createFields: FormFieldT<distributorT>[] = [
  {name: 'distributor_name', label: 'Distributor Name', type: 'text', placeholder: 'Distributor Name', required: true},
  {name: 'distributor_address', label: 'Address', type: 'text', placeholder: 'Address', required: true},
  {name: 'distributor_address2', label: 'Address 2', type: 'text', placeholder: 'Address 2', required: false},
  {name: 'distributor_city', label: 'City', type: 'text', placeholder: 'City', required: true},
  {
    name: 'distributor_state',
    label: 'State',
    type: 'select',
    placeholder: 'State',
    required: true,
    selectOptions: US_STATES_DATA,
  },
  {name: 'distributor_postal_code', label: 'Postal Code', type: 'text', placeholder: 'Postal Code', required: true},
  {
    name: 'distributor_license_number',
    label: 'License Number',
    type: 'text',
    placeholder: 'License Number',
    required: true,
  },
  {name: 'distributor_email', label: 'Email', type: 'email', placeholder: 'Email', required: true},
  {name: 'distributor_phone', label: 'Phone', type: 'text', placeholder: 'Phone', required: true},
  {
    name: 'distributor_geo_points',
    label: 'Geo Points',
    type: 'text',
    placeholder: 'Geo Points',
    required: false,
  },
  {
    name: 'distributor_metrc_api_key',
    label: 'Metrc API Key',
    type: 'text',
    placeholder: 'Metrc API Key',
    required: true,
  },
];

const editFields: FormFieldT<distributorT>[] = [...createFields];

function AdminDistributors() {
  const {isAdministrator} = useAuth();

  // State
  const [isVisible, setIsVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [marketSearchTerm, setMarketSearchTerm] = useState('');
  const [showMarketSearch, setShowMarketSearch] = useState(false);
  const [selectedMarkets, setSelectedMarkets] = useState<{market_id: number; market_name: string}[]>([]);

  // Refs
  const selectedDistributorIdRef = useRef<number | null>(null);
  const formDataRef = useRef<distributorInsertT>(initialFormData);

  const {createData, updateData, deleteData, getDistributor} = useDistributors();

  // Reactivity
  const {data: stats, refetch: refetchStats} = useViewQuery({
    view: 'distributor_stats',
  });

  /************************
   * QUERIES
   *************************/
  const {data: marketsData} = useDataQuery({
    table: 'Market',
    columns: ['market_id', 'market_name'],
    searchColumn: 'market_name',
    searchTerm: marketSearchTerm,
    enabled: isAdministrator,
  });

  const handleMarketSearch = (searchTerm: string) => {
    setMarketSearchTerm(searchTerm);
  };

  const handleMarketSelect = (market: {market_id: number; market_name: string}) => {
    setSelectedMarkets([...selectedMarkets, market]);
  };

  /************************
   * DISTRIBUTOR CRUD
   *************************/
  // Functions
  const handleCreate = async (data: distributorT) => {
    try {
      setIsSubmitting(true);
      await createData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to create distributor ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (data: distributorT) => {
    if (selectedDistributorIdRef.current === null) {
      setSubmissionError('No distributor selected');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to update distributor: ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedDistributorIdRef.current === null) {
      setDeleteError('No distributor selected');
      return;
    }

    try {
      setIsSubmitting(true);
      await deleteData(selectedDistributorIdRef.current);
      refetchStats();
      setIsDeleteModalVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to delete user ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (distributorId: number) => {
    const distributor = await getDistributor(distributorId);
    formDataRef.current = distributor;
    selectedDistributorIdRef.current = distributor.distributor_id;
    setIsEditing(true);
    setIsVisible(true);
  };

  const handleDeleteEvent = (distributorId: number) => {
    selectedDistributorIdRef.current = distributorId;
    setIsDeleteModalVisible(true);
  };

  // Stats data for stats component
  const statsRow: statsRowT = renderDistributorTableStats(stats?.total, stats?.active, stats?.inactive);

  return (
    <Page currentPage="Distributors">
      <StatsRowComponent stats={statsRow} />
      <DataTableRpc
        title="Distributors Summary"
        functionName="get_distributor_summary"
        columns={[
          {
            header: 'Name',
            field: 'distributor_name',
            render: row => (
              <Link
                className="text-primary hover:text-primary dark:text-primary"
                to={`/distributors/${row.distributor_id}`}
              >
                {row.distributor_name}
              </Link>
            ),
          },
          {
            header: 'Location',
            field: 'distributor_city',
            render: row => <p>{`${row.distributor_city}, ${row.distributor_state}`}</p>,
          },
          {
            header: 'Total Brands',
            field: 'total_brands',
          },
        ]}
        onAdd={() => {
          setShowMarketSearch(true);
        }}
        actions={[
          {
            icon: 'ri-pencil-line',
            color: 'info',
            handler: distributor => handleEdit(distributor.distributor_id),
            permission: () => isAdministrator,
          },
          {
            icon: 'ri-delete-bin-line',
            color: 'danger',
            handler: distributor => handleDeleteEvent(distributor.distributor_id),
            permission: () => isAdministrator,
          },
        ]}
        searchColumn="distributor_name"
      />
      <SearchAndSelectComponent
        title="Search and Select Market"
        isVisible={showMarketSearch}
        onClose={() => {
          setShowMarketSearch(false);
          setIsVisible(true);
        }}
        onSelect={item => {
          handleMarketSelect(item);
        }}
        overrideClose={true}
        mapStructure={{
          displayFields: [
            {
              title: 'Market Name',
              field: 'market_name',
            },
          ],
          valueField: 'market_id',
        }}
        onSearch={handleMarketSearch}
        results={marketsData?.data || []}
      />
      <ModalFormComponent
        isVisible={isVisible}
        fields={isEditing ? editFields : createFields}
        formData={formDataRef.current}
        onSubmit={(data: distributorT) => {
          setSubmissionError(null);
          if (isEditing) {
            handleUpdate(data);
          } else {
            handleCreate(data);
          }
        }}
        onClose={() => {
          setSubmissionError(null);
          setIsVisible(false);
        }}
        error={submissionError} // Pass the error to the modal
        isSubmitting={isSubmitting} // Pass the loading state to the modal
        title={isEditing ? 'Edit Distributor' : 'Create New Distributor'}
      />
      <ConfirmDeleteModalComponent
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        description="Are you sure you want to delete this distributor? This action cannot be undone."
        isSubmitting={isSubmitting}
        error={deleteError}
      />
    </Page>
  );
}

export default AdminDistributors;
